/**
 * CsrfClient — the CSRF token lifecycle every static App shares
 * (contract guidelines §4 + api-contract csrf.ts):
 *
 *   app boot        → bootstrap() once (before the first write)
 *   every write     → headers() → X-CSRF-Token on the request
 *   419 csrf_failed → withRefreshRetry() refreshes ONCE and retries ONCE
 *   login / logout  → clear() + bootstrap(): the token is bound to the
 *                     session (or anonymous pre-login state), so any
 *                     session change invalidates the old binding
 *
 * The token lives in memory only (plus the server-set csrf_token cookie
 * as a page-reload seed — see readCsrfCookie). There is no TTL math
 * client-side: the server owns expiry (12h default) and signals it via
 * 419; the client just reacts.
 */
import { HTTP_STATUS } from '@web-v4/api-contract';
import { CSRF_BOOTSTRAP_ENDPOINT, CSRF_HEADER_NAME, } from '@web-v4/api-contract/v1/auth';
import { ApiClient, ApiError } from '@web-v4/api-client';
/** Detects the contractual CSRF failure (error.code first, status as belt-and-braces). */
export function isCsrfFailure(error) {
    return (error instanceof ApiError &&
        (error.code === 'csrf_failed' || error.status === HTTP_STATUS.CSRF_FAILED));
}
export class CsrfClient {
    api;
    token;
    /** In-flight bootstrap, so concurrent ensureToken() calls share one GET. */
    inFlight;
    constructor(options) {
        this.api = options.api;
        this.token = options.initialToken;
    }
    /** The cached token, or undefined when never bootstrapped / cleared. */
    getToken() {
        return this.token;
    }
    /** The cached token if present, otherwise one bootstrap. */
    async ensureToken() {
        return this.token ?? this.bootstrap();
    }
    /**
     * GET /api/v1/auth/csrf — cached on success. Concurrent calls share a
     * single in-flight request (app boot fires me + csrf + captcha in
     * parallel; they must not race each other's writes).
     */
    bootstrap() {
        this.inFlight ??= this.doBootstrap();
        return this.inFlight;
    }
    /**
     * Forces a fresh bootstrap even when a token is cached — the 419
     * recovery path and the post login/logout re-binding. Concurrent
     * callers still share one request.
     */
    refresh() {
        this.clear();
        return this.bootstrap();
    }
    /** Drops the cached token (session binding changed, or logout). */
    clear() {
        this.token = undefined;
    }
    /** `{ X-CSRF-Token: <token> }` for one write request (ensures a token first). */
    async headers() {
        const token = await this.ensureToken();
        return { [CSRF_HEADER_NAME]: token };
    }
    /**
     * The contractual 419 dance: run `fn` with fresh CSRF headers; on a
     * csrf_failed/419 failure refresh the token ONCE and re-run ONCE. A
     * second failure (or any non-CSRF error) propagates untouched.
     *
     * `fn` receives the headers and performs the actual write through an
     * ApiClient, e.g.
     *
     *   csrf.withRefreshRetry((h) => api.request(LOGIN_ENDPOINT, { body, headers: h }))
     */
    async withRefreshRetry(fn) {
        try {
            return await fn(await this.headers());
        }
        catch (error) {
            if (!isCsrfFailure(error)) {
                throw error;
            }
        }
        await this.refresh();
        return fn(await this.headers());
    }
    async doBootstrap() {
        try {
            const envelope = await this.api.request(CSRF_BOOTSTRAP_ENDPOINT);
            this.token = envelope.data.csrf_token;
            return this.token;
        }
        finally {
            this.inFlight = undefined;
        }
    }
}
//# sourceMappingURL=client.js.map